import type { GameDefinition, Outcome, Rng, Seat, Viewer } from '@campfire/engine';
import { shoe, shuffle, type Card } from '@campfire/cards';
import { ACE_EITHER_RUNS, aceIsLow, bestArrangement, candidateMelds, layoffOptions, meldKind, orderMeld, pipValue, type Arrangement, type MeldKind } from '@campfire/melds';

const RULES = ACE_EITHER_RUNS;

export interface Rummy500Options {
  /** Points to win the game. */
  readonly target: number;
}

/** A meld on the table. Anyone may lay off on it; each card scores for whoever placed it. */
export interface TableMeld {
  readonly kind: MeldKind;
  /** Display order (runs low→high). */
  readonly cards: readonly Card[];
  /** Seat that placed each card (parallel to `cards`). */
  readonly by: readonly Seat[];
}

export type Rummy500Event =
  | { readonly kind: 'draw'; readonly seat: Seat }
  | { readonly kind: 'take'; readonly seat: Seat; readonly cards: readonly Card[] }
  | { readonly kind: 'meld'; readonly seat: Seat; readonly cards: readonly Card[] }
  | { readonly kind: 'layoff'; readonly seat: Seat; readonly cards: readonly Card[]; readonly meld: number }
  | { readonly kind: 'discard'; readonly seat: Seat; readonly card: Card }
  | {
      readonly kind: 'round';
      readonly round: number;
      /** Who went out, or null when the stock ran out. */
      readonly out: Seat | null;
      /** Per seat: points melded, points left in hand, net change. */
      readonly melded: readonly number[];
      readonly inHand: readonly number[];
      readonly net: readonly number[];
    };

export interface Rummy500State {
  readonly options: Rummy500Options;
  readonly hands: readonly (readonly Card[])[];
  /** Index 0 is the top. */
  readonly stock: readonly Card[];
  /** Spread face up, index 0 is the bottom (oldest), last is the top. */
  readonly discard: readonly Card[];
  readonly melds: readonly TableMeld[];
  readonly turn: Seat;
  readonly phase: 'draw' | 'play';
  /** The deepest card taken from the discard pile: it must be melded before anything else. */
  readonly mustUse: string | null;
  readonly dealer: Seat;
  readonly round: number;
  readonly scores: readonly number[];
  readonly last: Rummy500Event | null;
  /** The previous round's scoring, kept for display. */
  readonly lastRound: Extract<Rummy500Event, { kind: 'round' }> | null;
  readonly result: Outcome | null;
}

export type Rummy500Action =
  | { readonly type: 'draw' }
  /** Take `card` from the discard pile and every card above it. */
  | { readonly type: 'take'; readonly card: string }
  | { readonly type: 'meld'; readonly cards: readonly string[] }
  | { readonly type: 'layoff'; readonly cards: readonly string[]; readonly meld: number }
  | { readonly type: 'discard'; readonly card: string };

export interface Rummy500View {
  readonly you: Seat | null;
  readonly hand: readonly Card[] | null;
  /** Suggested melds and leftover cards for your hand (ace counted 15). */
  readonly arrangement: Arrangement | null;
  /** Discard-pile cards you could take now (their deepest card can be melded). */
  readonly takeable: readonly string[];
  readonly handCounts: readonly number[];
  readonly stockCount: number;
  readonly discard: readonly Card[];
  readonly melds: readonly TableMeld[];
  readonly turn: Seat;
  readonly phase: 'draw' | 'play';
  readonly mustUse: string | null;
  readonly dealer: Seat;
  readonly round: number;
  readonly scores: readonly number[];
  /** Points each seat has on the table this round. */
  readonly tablePoints: readonly number[];
  readonly target: number;
  readonly last: Rummy500Event | null;
  readonly lastRound: Extract<Rummy500Event, { kind: 'round' }> | null;
  readonly result: Outcome | null;
}

/** Ace 15 (but 1 at the bottom of A-2-3), faces 10, pips face value (FR-RUMMY-07). */
export const rummyPoints = (c: Card, lowAce = false): number => (c.rank === 'A' ? (lowAce ? 1 : 15) : pipValue(c));

/** Points of each placed card on a table meld. */
function meldPoints(m: TableMeld): number[] {
  const low = m.kind === 'run' && aceIsLow(m.cards);
  return m.cards.map((c) => rummyPoints(c, low));
}

const handSize = (seats: number) => (seats === 2 ? 13 : 7);

function deal(options: Rummy500Options, seats: number, dealer: Seat, round: number, scores: readonly number[], lastRound: Rummy500State['lastRound'], rng: Rng): Rummy500State {
  // Five or more players use a double deck.
  const deck = shuffle(shoe(seats >= 5 ? 2 : 1), rng);
  const n = handSize(seats);
  return {
    options,
    hands: Array.from({ length: seats }, (_, i) => deck.slice(i * n, i * n + n)),
    discard: [deck[seats * n] as Card],
    stock: deck.slice(seats * n + 1),
    melds: [],
    turn: (dealer + 1) % seats,
    phase: 'draw',
    mustUse: null,
    dealer,
    round,
    scores,
    last: null,
    lastRound,
    result: null,
  };
}

/** Can `card` go down this turn using `pool` (hand plus the cards taken) — in a new meld or on a table meld? */
export function canUse(card: Card, pool: readonly Card[], melds: readonly TableMeld[]): boolean {
  const has = (g: readonly Card[]) => g.some((c) => c.id === card.id);
  return candidateMelds(pool, RULES).some(has) || melds.some((m) => layoffOptions(pool, m.cards, RULES).some(has));
}

const pick = (hand: readonly Card[], ids: readonly string[]): Card[] | null => {
  if (new Set(ids).size !== ids.length) return null;
  const out = ids.map((id) => hand.find((c) => c.id === id));
  return out.every((c) => c !== undefined) ? (out as Card[]) : null;
};

const tablePoints = (s: Rummy500State): number[] =>
  s.hands.map((_, seat) => s.melds.reduce((sum, m) => sum + meldPoints(m).reduce((p, v, i) => p + (m.by[i] === seat ? v : 0), 0), 0));

function endRound(s: Rummy500State, out: Seat | null, rng: Rng): Rummy500State {
  const melded = tablePoints(s);
  const inHand = s.hands.map((h) => h.reduce((p, c) => p + rummyPoints(c), 0));
  const net = melded.map((m, i) => m - (inHand[i] as number));
  const scores = s.scores.map((v, i) => v + (net[i] as number));
  const last: Extract<Rummy500Event, { kind: 'round' }> = { kind: 'round', round: s.round, out, melded, inHand, net };
  const top = Math.max(...scores);
  if (top >= s.options.target) {
    const winners = scores.flatMap((v, i) => (v === top ? [i] : []));
    return { ...s, scores, last, lastRound: last, result: { winners, draw: false, reason: 'target', scores: { ...scores } } };
  }
  return { ...deal(s.options, s.hands.length, (s.dealer + 1) % s.hands.length, s.round + 1, scores, last, rng), last };
}

/** The top discard is always free to take (FR-RUMMY-10); a deeper card only if it can be melded now. */
const takeable = (s: Rummy500State, seat: Seat): string[] =>
  s.discard.filter((c, i) => i === s.discard.length - 1 || canUse(c, [...(s.hands[seat] ?? []), ...s.discard.slice(i)], s.melds)).map((c) => c.id);

export const rummy500: GameDefinition<Rummy500State, Rummy500Action, Rummy500View, Rummy500Options> = {
  id: 'rummy500',
  name: 'Rummy 500',
  rules: [
    'Score points by melding cards. The first player to reach the target wins. Melds are sets (3 or 4 of a kind) and runs (3 or more in a suit); the ace may be low (A-2-3) or high (Q-K-A) but not both.',
    'Hands are 7 cards (13 when there are two players); with 5 or more players two decks are used. One card is turned up to start the discard pile, which is spread so everyone can see it.',
    'A turn is: draw one card from the stock, or take any card from the discard pile along with every card above it. Then you may meld, lay off, and finally discard one card.',
    'To take from the pile you must be able to use the deepest card you take straight away in a meld or lay-off, and you must do so before you do anything else.',
    'You may lay off cards onto any meld on the table, yours or anyone else’s. Each card scores for the player who put it down.',
    'Scoring: ace is 15 (1 in A-2-3), faces are 10, other cards face value. At the end of a round you add your melded cards and subtract the cards still in your hand.',
    'A round ends when a player gets rid of their last card, by melding, laying off or discarding. It also ends when the stock runs out. There is no bonus for going out.',
    'Setting: Game length. First to 500 or to 250 points; the highest score wins.',
    'Not in the app: a minimum of points before your first meld. Many groups require 50 or more; the app doesn’t enforce this, so agree on it at the table.',
  ],
  minSeats: 2,
  maxSeats: 8,
  defaultOptions: { target: 500 },
  optionSpecs: [
    {
      key: 'target',
      label: 'Game length',
      help: 'Melded cards score for you, cards left in your hand count against you (ace 15, faces 10).',
      choices: [
        { value: 500, label: 'First to 500 points' },
        { value: 250, label: 'First to 250 points' },
      ],
    },
  ],

  setup({ seats, options, rng }) {
    return deal(options, seats, 0, 1, Array.from({ length: seats }, () => 0), null, rng);
  },

  activeSeats: (s) => (s.result ? [] : [s.turn]),

  validate(s, a, seat) {
    if (seat !== s.turn) return { ok: false, reason: 'not your turn' };
    const hand = s.hands[seat] ?? [];
    if (a.type === 'draw' || a.type === 'take') {
      if (s.phase !== 'draw') return { ok: false, reason: 'you have already drawn' };
      if (a.type === 'draw') return { ok: true };
      const at = s.discard.findIndex((c) => c.id === a.card);
      if (at < 0) return { ok: false, reason: 'that card is not in the discard pile' };
      if (at < s.discard.length - 1 && !canUse(s.discard[at] as Card, [...hand, ...s.discard.slice(at)], s.melds)) {
        return { ok: false, reason: 'you can only take a card you can meld right away' };
      }
      return { ok: true };
    }
    if (a.type !== 'meld' && a.type !== 'layoff' && a.type !== 'discard') return { ok: false, reason: 'unknown action' };
    if (s.phase !== 'play') return { ok: false, reason: 'draw first' };
    if (a.type === 'discard') {
      if (s.mustUse) return { ok: false, reason: 'meld the card you took from the pile first' };
      return hand.some((c) => c.id === a.card) ? { ok: true } : { ok: false, reason: 'you do not hold that card' };
    }
    const cards = Array.isArray(a.cards) ? pick(hand, a.cards) : null;
    if (!cards || cards.length === 0) return { ok: false, reason: 'pick cards from your hand' };
    if (s.mustUse && !a.cards.includes(s.mustUse)) return { ok: false, reason: 'meld the card you took from the pile first' };
    if (a.type === 'meld') return meldKind(cards, RULES) ? { ok: true } : { ok: false, reason: 'not a set or a run' };
    const target = s.melds[a.meld];
    if (!target) return { ok: false, reason: 'no such meld' };
    return meldKind([...target.cards, ...cards], RULES) === target.kind ? { ok: true } : { ok: false, reason: 'those cards don’t fit that meld' };
  },

  apply(s, a, { rng }) {
    const seat = s.turn;
    const hand = s.hands[seat] as readonly Card[];
    const setHand = (h: readonly Card[]) => s.hands.map((x, i) => (i === seat ? h : x));
    if (a.type === 'draw') {
      return { ...s, hands: setHand([...hand, s.stock[0] as Card]), stock: s.stock.slice(1), phase: 'play', last: { kind: 'draw', seat } };
    }
    if (a.type === 'take') {
      const at = s.discard.findIndex((c) => c.id === a.card);
      const taken = s.discard.slice(at);
      return {
        ...s,
        hands: setHand([...hand, ...taken]),
        discard: s.discard.slice(0, at),
        phase: 'play',
        mustUse: taken.length > 1 ? a.card : null,
        last: { kind: 'take', seat, cards: taken },
      };
    }
    if (a.type === 'discard') {
      const card = hand.find((c) => c.id === a.card) as Card;
      const st: Rummy500State = { ...s, hands: setHand(hand.filter((c) => c.id !== card.id)), discard: [...s.discard, card], last: { kind: 'discard', seat, card } };
      if ((st.hands[seat] as Card[]).length === 0) return endRound(st, seat, rng);
      if (st.stock.length === 0) return endRound(st, null, rng);
      return { ...st, phase: 'draw', turn: (seat + 1) % s.hands.length };
    }
    const cards = pick(hand, a.cards) as Card[];
    const rest = hand.filter((c) => !a.cards.includes(c.id));
    const mustUse = s.mustUse && a.cards.includes(s.mustUse) ? null : s.mustUse;
    let melds: TableMeld[];
    let last: Rummy500Event;
    if (a.type === 'meld') {
      const ordered = orderMeld(cards, RULES);
      melds = [...s.melds, { kind: meldKind(cards, RULES) as MeldKind, cards: ordered, by: ordered.map(() => seat) }];
      last = { kind: 'meld', seat, cards: ordered };
    } else {
      const target = s.melds[a.meld] as TableMeld;
      const owner = new Map(target.cards.map((c, i) => [c.id, target.by[i] as Seat]));
      const ordered = orderMeld([...target.cards, ...cards], RULES);
      melds = s.melds.map((m, i) => (i === a.meld ? { ...m, cards: ordered, by: ordered.map((c) => owner.get(c.id) ?? seat) } : m));
      last = { kind: 'layoff', seat, cards, meld: a.meld };
    }
    const st: Rummy500State = { ...s, hands: setHand(rest), melds, mustUse, last };
    return rest.length === 0 ? endRound(st, seat, rng) : st;
  },

  view(s, viewer: Viewer) {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    const hand = you === null ? null : (s.hands[you] ?? null);
    const mine = you !== null && you === s.turn && !s.result;
    return {
      you,
      hand,
      arrangement: hand ? bestArrangement(hand, { rules: RULES, value: (c) => rummyPoints(c) }) : null,
      takeable: mine && s.phase === 'draw' ? takeable(s, you) : [],
      handCounts: s.hands.map((h) => h.length),
      stockCount: s.stock.length,
      discard: s.discard,
      melds: s.melds,
      turn: s.turn,
      phase: s.phase,
      mustUse: s.mustUse,
      dealer: s.dealer,
      round: s.round,
      scores: s.scores,
      tablePoints: tablePoints(s),
      target: s.options.target,
      last: s.last,
      lastRound: s.lastRound,
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.result || seat !== s.turn) return [];
    if (s.phase === 'draw') return [{ type: 'draw' }, ...takeable(s, seat).map((card) => ({ type: 'take', card }) as const)];
    const hand = s.hands[seat] ?? [];
    const out: Rummy500Action[] = [];
    const ok = (ids: readonly string[]) => !s.mustUse || ids.includes(s.mustUse);
    for (const m of candidateMelds(hand, RULES)) {
      const ids = m.map((c) => c.id);
      if (ok(ids)) out.push({ type: 'meld', cards: ids });
    }
    s.melds.forEach((m, i) => {
      for (const g of layoffOptions(hand, m.cards, RULES)) {
        const ids = g.map((c) => c.id);
        if (ok(ids)) out.push({ type: 'layoff', cards: ids, meld: i });
      }
    });
    if (!s.mustUse) for (const c of hand) out.push({ type: 'discard', card: c.id });
    return out;
  },
};
